import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environment/environment';

/**
 * Exchanges an attachment file name for a short-lived, pre-signed download URL.
 *
 * Attachments are not served directly; the backend returns a `secure_url` that
 * the caller then opens.
 */
@Injectable({
  providedIn: 'root'
})
export class FileAttachmentService {

  constructor(private readonly http: HttpClient) {
  }

  /**
   * Base API URL for the current domain.
   *
   * Returns the live URL when the `Domain` session code is `L`, otherwise the
   * default ticketing API.
   *
   * Note: the name is misspelled (`tickeAPI`) in the original source.
   */
  private get tickeAPI(): string {
    const domain = sessionStorage.getItem('Domain');
    if (domain === 'L') {
      return environment.liveURL;
    } else {
      return environment.ticketAPI;
    }
  }

  /**
   * Requests a secure download URL for a stored attachment.
   *
   * Note: the name is misspelled (`donwloadFile`) in the original source.
   *
   * @param fileName Stored name of the attachment to resolve.
   * @returns Response containing a `secure_url` to open in a new tab.
   * @throws If no `auth_token` is present in `sessionStorage`.
   */
  donwloadFile(fileName: string): Observable<any> {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('[FileAttachmentService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const url = `${this.tickeAPI}/secure-attachment/`;
    const formData = new FormData();
    formData.append('file_name', fileName);

    return this.http.post(
      url,
      formData,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
        }),
      }
    )
  }

  /**
   * Resolves a secure URL for an inline (embedded) attachment.
   *
   * Note: this is currently byte-for-byte identical to {@link donwloadFile} — it
   * posts the same `file_name` field to the same `secure-attachment` endpoint —
   * and despite the name it uploads nothing.
   *
   * @param fileName Stored name of the attachment to resolve.
   * @returns Response containing a `secure_url`.
   * @throws If no `auth_token` is present in `sessionStorage`.
   */
  inlineFilesUpload(fileName: string): Observable<any> {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('[FileAttachmentService] Session expired or missing required session data.');
      throw new Error('Session expired or missing required session data. Please log in again.');
    }

    const url = `${this.tickeAPI}/secure-attachment/`;
    const formData = new FormData();
    formData.append('file_name', fileName);

    return this.http.post(
      url,
      formData,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${authToken}`,
        }),
      }
    )
  }
}
